import React, { useState, useMemo } from "react";
import {
  Box, Typography, Grid, CircularProgress, alpha, useTheme, Chip, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider,
} from "@mui/material";
import { StepUpSpinner } from "../../Components/Loader";
import {
  useGetCashFlowReportQuery, useGetRevenueReportQuery, useGetFinanceExpensesQuery,
} from "../../Api/attoDeskApi";
import AppDatePicker from "../../Components/DatePicker/AppDatePicker";
import dayjs from "dayjs";

import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import DownloadIcon from "@mui/icons-material/Download";

const exportCSV = (data: any[], filename: string) => {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const csv = [headers.join(","), ...data.map(r => headers.map(h => `"${r[h] ?? ""}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
};

type Tab = "cashflow" | "revenue" | "expenses" | "profit";

const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
  { key: "cashflow", label: "Cash Flow", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
  { key: "revenue", label: "Revenue", icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
  { key: "expenses", label: "Expenses", icon: <TrendingDownIcon sx={{ fontSize: 16 }} /> },
  { key: "profit", label: "Profit", icon: <BarChartIcon sx={{ fontSize: 16 }} /> },
];

const MetricCard: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid rgba(0,0,0,0.06)", px: 2.5, py: 1.5, minWidth: 140 }}>
    <Typography sx={{ fontSize: 10, fontWeight: 600, color: "text.disabled", textTransform: "uppercase" }}>{label}</Typography>
    <Typography sx={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1, mt: 0.25 }}>${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
  </Box>
);

const Reports = () => {
  const theme = useTheme();
  const [tab, setTab] = useState<Tab>("cashflow");
  const [startDate, setStartDate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));
  const [year, setYear] = useState(dayjs().year());

  const { data: cfData, isLoading: cfLoading } = useGetCashFlowReportQuery({ startDate, endDate }, { skip: tab !== "cashflow" && tab !== "profit" });
  const { data: revData, isLoading: revLoading } = useGetRevenueReportQuery({ year }, { skip: tab !== "revenue" && tab !== "profit" });
  const { data: expData, isLoading: expLoading } = useGetFinanceExpensesQuery({ startDate, endDate }, { skip: tab !== "expenses" && tab !== "profit" });

  const cashFlow = useMemo(() => (cfData?.data as any) || {}, [cfData]);
  const revenue = useMemo(() => (revData?.data as any) || {}, [revData]);
  const expenseData = useMemo(() => (expData?.data as any) || {}, [expData]);

  const isLoading = tab === "cashflow" ? cfLoading : tab === "revenue" ? revLoading : tab === "expenses" ? expLoading : cfLoading || revLoading || expLoading;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em" }}>Finance</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", mt: 0.25 }}>Reports</Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ display: "flex", bgcolor: "rgba(0,0,0,0.03)", borderRadius: "12px", p: "3px", mb: 2, width: "fit-content" }}>
        {tabs.map((t) => (
          <Button key={t.key} size="small" startIcon={t.icon} onClick={() => setTab(t.key)}
            sx={{
              textTransform: "none", fontSize: 12, borderRadius: "10px", px: 2, py: 0.75,
              bgcolor: tab === t.key ? "#fff" : "transparent",
              color: tab === t.key ? theme.palette.primary.main : "text.secondary",
              boxShadow: tab === t.key ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              fontWeight: tab === t.key ? 700 : 500,
            }}>
            {t.label}
          </Button>
        ))}
      </Box>

      {/* Date filters */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap", alignItems: "center" }}>
        {tab !== "revenue" ? (
          <>
            <AppDatePicker size="small" fullWidth={false} label="From" value={startDate} onChange={setStartDate}
              sx={{ width: 150, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 12 } }} />
            <AppDatePicker size="small" fullWidth={false} label="To" value={endDate} onChange={setEndDate}
              sx={{ width: 150, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 12 } }} />
          </>
        ) : (
          <TextField size="small" type="number" label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))}
            InputLabelProps={{ shrink: true }} sx={{ width: 100, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 12 } }} />
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><StepUpSpinner size="medium" /></Box>
      ) : (
        <>
          {/* ── Cash Flow Tab ── */}
          {tab === "cashflow" && (
            <Box>
              <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
                <MetricCard label="Cash In" value={Number(cashFlow.cashIn || 0)} color="#22c55e" />
                <MetricCard label="Cash Out" value={Number(cashFlow.cashOut || 0)} color="#ef4444" />
                <MetricCard label="Net Cash Flow" value={Number(cashFlow.netCashFlow || 0)} color={(cashFlow.netCashFlow || 0) >= 0 ? "#22c55e" : "#ef4444"} />
              </Box>

              {/* By Category */}
              <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden", mb: 2 }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.05)", display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>By Category</Typography>
                  <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />} onClick={() => exportCSV((cashFlow.byCategory || []), `cashflow-${startDate}.csv`)}
                    sx={{ textTransform: "none", fontSize: 11 }}>Export</Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {["Category", "Credits", "Debits", "Count"].map(h => (
                          <TableCell key={h} sx={{ fontSize: 10.5, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", py: 1.25, px: 2 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(cashFlow.byCategory || []).map((row: any) => (
                        <TableRow key={row.category}>
                          <TableCell sx={{ fontSize: 12.5, fontWeight: 600, px: 2, py: 1, textTransform: "capitalize" }}>{row.category}</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#22c55e", px: 2, py: 1 }}>${Number(row.credits || 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#ef4444", px: 2, py: 1 }}>${Number(row.debits || 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: "text.secondary", px: 2, py: 1 }}>{row.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}

          {/* ── Revenue Tab ── */}
          {tab === "revenue" && (
            <Box>
              <MetricCard label={`Total Revenue ${year}`} value={Number(revenue.totalRevenue || 0)} color="#22c55e" />
              <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden", mt: 2 }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.05)", display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Monthly Revenue</Typography>
                  <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />} onClick={() => exportCSV((revenue.monthly || []).map((m: any) => ({ Month: m.label, Revenue: m.revenue })), `revenue-${year}.csv`)}
                    sx={{ textTransform: "none", fontSize: 11 }}>Export</Button>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Grid container spacing={1.5}>
                    {(revenue.monthly || []).map((m: any) => (
                      <Grid item xs={6} sm={4} md={3} lg={2} key={m.month}>
                        <Box sx={{ textAlign: "center", p: 1.5, borderRadius: "10px", bgcolor: m.revenue > 0 ? alpha("#22c55e", 0.04) : "rgba(0,0,0,0.015)", border: `1px solid ${m.revenue > 0 ? alpha("#22c55e", 0.1) : "rgba(0,0,0,0.04)"}` }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 600, color: "text.disabled", textTransform: "uppercase" }}>{m.label?.slice(0, 3)}</Typography>
                          <Typography sx={{ fontSize: 16, fontWeight: 700, color: m.revenue > 0 ? "#22c55e" : "text.disabled", mt: 0.25 }}>${Number(m.revenue || 0).toFixed(0)}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            </Box>
          )}

          {/* ── Expenses Tab ── */}
          {tab === "expenses" && (
            <Box>
              <MetricCard label="Total Expenses" value={Number(expenseData.total || 0)} color="#ef4444" />
              <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden", mt: 2 }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.05)", display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Expense Details</Typography>
                  <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
                    onClick={() => exportCSV((expenseData.expenses || []).map((e: any) => ({ Title: e.title, Amount: e.amount, Category: e.category, Date: e.expense_date, Vendor: e.vendor || "" })), `expenses-${startDate}.csv`)}
                    sx={{ textTransform: "none", fontSize: 11 }}>Export</Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {["Title", "Category", "Amount", "Date", "Vendor"].map(h => (
                          <TableCell key={h} sx={{ fontSize: 10.5, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", py: 1.25, px: 2 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(expenseData.expenses || []).map((e: any) => (
                        <TableRow key={e.id}>
                          <TableCell sx={{ fontSize: 12.5, fontWeight: 500, px: 2, py: 1 }}>{e.title}</TableCell>
                          <TableCell sx={{ px: 2, py: 1 }}><Chip label={e.category} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: "rgba(0,0,0,0.04)", textTransform: "capitalize" }} /></TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#ef4444", px: 2, py: 1 }}>${Number(e.amount).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: "text.secondary", px: 2, py: 1 }}>{e.expense_date ? dayjs(e.expense_date).format("MMM D") : ""}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: "text.secondary", px: 2, py: 1 }}>{e.vendor || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}

          {/* ── Profit Tab ── */}
          {tab === "profit" && (
            <Box>
              <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
                <MetricCard label="Revenue (Cash In)" value={Number(cashFlow.cashIn || 0)} color="#22c55e" />
                <MetricCard label="Expenses (Cash Out)" value={Number(cashFlow.cashOut || 0)} color="#ef4444" />
                <MetricCard label="Net Profit" value={Number(cashFlow.netCashFlow || 0)} color={(cashFlow.netCashFlow || 0) >= 0 ? "#22c55e" : "#ef4444"} />
              </Box>

              <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(0,0,0,0.06)", p: 2.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 2 }}>Profit Analysis</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>Income Sources</Typography>
                    {(cashFlow.byCategory || []).filter((c: any) => c.credits > 0).map((c: any) => (
                      <Box key={c.category} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                        <Typography sx={{ fontSize: 12, textTransform: "capitalize" }}>{c.category}</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#22c55e" }}>${Number(c.credits).toFixed(2)}</Typography>
                      </Box>
                    ))}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>Expense Sources</Typography>
                    {(cashFlow.byCategory || []).filter((c: any) => c.debits > 0).map((c: any) => (
                      <Box key={c.category} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                        <Typography sx={{ fontSize: 12, textTransform: "capitalize" }}>{c.category}</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#ef4444" }}>${Number(c.debits).toFixed(2)}</Typography>
                      </Box>
                    ))}
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Reports;
